<template>
  <v-dialog
    v-model="localDialog"
    max-width="800px"
    min-height="500px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6 font-weight-bold">
          {{ isEditMode ? 'Send Document' : 'Send Document' }}
        </span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="closeDialog"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <v-form ref="documentForm" v-model="formValid">
          <v-row>
            <!-- Subject -->
            <v-col cols="12" md="7">
              <v-text-field
                v-model="subject.value.value"
                label="Subject"
                variant="outlined"
                density="compact"
                hide-details="auto"
                :error-messages="subject.errorMessage.value"
              />
            </v-col>

            <!-- Date -->
            <v-col cols="12" md="5">
              <v-date-input
                v-model="date.value.value"
                label="Date"
                density="compact"
                variant="outlined"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar-month"
                hide-details="auto"
                :error-messages="date.errorMessage.value"
              />
            </v-col>

            <v-col cols="12" md="12" class="pa-0">
                  <v-checkbox
                    v-model="is_mail.value.value"
                    label="Sent Mail"
                    color="primary"
                    hide-details
                  ></v-checkbox>
            </v-col>

             <v-col v-if="is_mail.value.value" cols="12" md="12">
              <v-divider></v-divider>
                <v-combobox
                  v-model="to.value.value"
                  :items="emailSuggestions"
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :error-messages="to.errorMessage.value"
                  label="To"
                  multiple
                  chips
                  closable-chips
                  hint="Type email and press enter"
                  persistent-hint
                  autocomplete="off"
                />
                <div class="text-caption text-grey mt-1">
                  {{ to.value.value?.length }} recipient(s)
                </div>
              </v-col>

              <!-- CC -->
               <v-col v-if="is_mail.value.value" cols="12" md="12">
                 <v-expand-transition>
                   <v-row v-if="showCc" class="mb-2">
                      <v-col cols="12">
                       <v-combobox
                         v-model="cc.value.value"
                         :items="emailSuggestions"
                         density="compact"
                         variant="outlined"
                         label="Cc"
                         multiple
                         chips
                         closable-chips
                       />
                     </v-col>
                   </v-row>
                 </v-expand-transition>
   
                 <v-row>
                   <v-col cols="12">
                     <v-btn
                       size="small"
                       variant="text"
                       @click="showCc = !showCc"
                     >
                       Add Cc
                     </v-btn>
                   </v-col>
                 </v-row>

                 <v-divider></v-divider>
               </v-col>

            <!-- Message -->
            <v-col cols="12">
              <v-label class="text-subtitle-1 font-weight-bold mb-1">
                Message
              </v-label>
              <Ckeditor
                :editor="ClassicEditor"
                v-model="message.value.value"
                :config="editorConfig"
              />
            </v-col>

            <!-- Draft -->
            <v-col cols="12">
              <v-divider class="my-2" />
              <div class="text-subtitle-2 mb-2">Document</div>

              <v-textarea
                v-model="draft.value.value"
                variant="outlined"
                density="compact"
                rows="3"
                auto-grow
                hide-details="auto"
                :error-messages="draft.errorMessage.value"
              />
            </v-col>

            <!-- Approval Matrix Selection -->
            <v-col cols="12">
              <v-divider class="my-2" />
              <div class="text-subtitle-2 mb-2">Approval Flow</div>

              <v-autocomplete
                v-model="approval_matrix_id.value.value"
                :items="getApprovalMatrixList"
                :item-title="formatApprovalMatrixItem"
                item-value="id"
                label="Select Approval Matrix"
                variant="outlined"
                density="compact"
                autocomplete="off"
                hide-details="auto"
                :error-messages="approval_matrix_id.errorMessage.value"
                clearable
                @update:modelValue="fetchApprovalMatrixDetails"
              />
            </v-col>

            <!-- Approval Workflow Display -->
            <v-col cols="12" v-if="matrix && matrix.details && matrix.details.length">
              <v-timeline align="start" side="end">
                <v-timeline-item 
                  v-for="(detail, index) in matrix.details" 
                  :key="index"
                  dot-color="teal-lighten-3" 
                  size="small"
                >
                  <div class="d-flex flex-column">
                    <div class="text-body-2 font-weight-bold">Step {{ detail.sl }}</div>
                    <div class="text-caption">{{ detail.employee?.name || 'N/A' }} ({{ detail.employee?.code || 'N/A' }})</div>
                    <div class="text-caption text-grey" v-if="detail.can_edit">Can Edit</div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-col>

          </v-row>

          <!-- Attached Files -->
          <v-row>
            <v-col cols="12">
              <v-divider class="my-2" />
              <div class="text-subtitle-2 mb-3">Attached Files</div>

              <v-row v-if="selectedFile.length" dense>
                <v-col
                  v-for="file in selectedFile"
                  :key="file.id"
                  cols="12"
                  md="4"
                >
                  <v-card
                    variant="tonal"
                    class="d-flex align-center pa-3 file-card"
                    rounded="lg"
                    :color="getFileColor(file)"
                      @click="downloadFile(file, 'main_file')"
                      style="cursor: pointer;"
                  >
                    <v-avatar
                      size="42"
                      class="mr-3"
                      :color="getFileColor(file)"
                      variant="tonal"
                    >
                      <v-icon size="22">
                        {{ getFileIcon(file) }}
                      </v-icon>
                    </v-avatar>

                    <div class="flex-grow-1">
                      <div class="text-body-2 font-weight-medium">
                        {{ file.original_name }}
                      </div>
                      <div class="text-caption text-grey">
                        {{ file.extension?.toUpperCase() }}
                      </div>
                    </div>

                    <v-btn
                      icon
                      variant="tonal"
                      color="primary"
                      size="small"
                      @click="downloadFile(file, 'main_file')"
                    >
                      <v-icon>mdi-download</v-icon>
                    </v-btn>
                  </v-card>
                </v-col>
              </v-row>

              <div v-else class="text-grey text-caption">
                No files attached
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="text"
          color="grey-darken-1"
          @click="closeDialog"
          :disabled="isSaving || isUploading"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          @click="sendDocument"
          :loading="isSaving"
          :disabled="!formValid"
        >
          {{ isEditMode ? 'Update' : 'Send' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Ckeditor } from "@ckeditor/ckeditor5-vue";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { ref, watch, computed } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";
import { useStore } from "vuex";
import formattedDate from "@/plugins/dateFormatter";
import {downlosdCmsFileUrl} from "@/store/api";
import axios from "axios";
import { prefix } from "@/store/api";
import { useRouter } from 'vue-router'

export default {
  name: "CmsDocumentUploadModal",
  components: { Ckeditor },
  props: {
    dialog: Boolean,
    documentData: [Array, Object],
    isEditMode: Boolean,
  },
  emits: ["update:dialog", "refresh"],

  setup(props, { emit }) {
    const store = useStore();
    const router = useRouter()

    const localDialog = computed({
      get: () => props.dialog,
      set: (val) => emit("update:dialog", val),
    });

    const formValid = ref(false);
    const selectedFile = ref([]);
    const isSaving = ref(false);
    const isUploading = ref(false);
    const doc_ids = ref("");
    const matrix = ref(null);
    const approvalMatrixList = ref([]);
    

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        subject: yup.string().required(),
        date: yup.date().required(),
        message: yup.string().nullable(),
        draft: yup.string().required(),
        approval_matrix_id: yup.number().required(),
      }),
    });

    const subject = useField("subject");
    const to = useField("to");
    const cc = useField("cc");
    const showCc = ref(false);
    const showBcc = ref(false);
    const date = useField("date");
    const message = useField("message");
    const draft = useField("draft");
    const is_mail = useField("is_mail");
    const approval_matrix_id = useField("approval_matrix_id");

    to.value.value = [];
    cc.value.value = [];

    watch(
      () => props.documentData,
      (data) => {
        if (Array.isArray(data) && data.length) {
          selectedFile.value = data;
          subject.value.value = data ? data[0]?.title : '';
          doc_ids.value = data.map(i => i.id).filter(Boolean).join(",");
          draft.value.value = data
            .map(i => i.original_name)
            .filter(Boolean)
            .join(", \n");
        } else {
          selectedFile.value = [];
          doc_ids.value = "";
          draft.value.value = "";
        }
      },
      { immediate: true }
    );

    const resetFormData = () => {
      resetForm();
      selectedFile.value = [];
      doc_ids.value = "";
    };

    const usersList = computed(() => store.getters.getUsersList);

    const getApprovalMatrixList = computed(() => {
      return approvalMatrixList.value;
    });

    const emailSuggestions = computed(() => {
      const suggestions = new Set();

      // Add users
      if (Array.isArray(usersList.value)) {
          usersList.value.forEach(user => {
            if (user.email) suggestions.add(user.email);
          });
      }
                
      // Add project customers/contacts if available
      // if (Array.isArray(projectList.value)) {
      //   projectList.value.forEach(proj => {
      //         if (proj.sales_orders && proj.sales_orders.email) {
      //           suggestions.add(proj.sales_orders.email);
      //         }
      //       });
      // }

      return Array.from(suggestions);
    });

    const approvalMatrix = async (id) => {
      try {
        const cmatrix = await axios.get( `${prefix}administration/get-approval-matrix-by-id/${id}`);
        matrix.value = cmatrix.data.data;
        
      } catch (error) {
        console.error("Download failed", error);
      }
    }

    const formatApprovalMatrixItem = (item) => {
      if (!item) return '';
      const name = item.approval_name?.name || 'N/A';
      const code = item.code || '';
      const branches = (item.details || [])
        .map(d => d?.employee?.branch?.name)
        .filter(Boolean);
      const uniqueBranches = Array.from(new Set(branches));
      const branchStr = uniqueBranches.length ? ', ' + uniqueBranches.join('-> ') : '';
      return `${name} - ${code}${branchStr}`;
    };

    const fetchApprovalMatrices = async () => {
      try {
        const response = await axios.get(`${prefix}administration/get-approval-matrix`);
        approvalMatrixList.value = response.data.data || [];
      } catch (error) {
        console.error("Failed to fetch approval matrices:", error);
        toast.error("Failed to load approval matrices");
      }
    };

    const fetchApprovalMatrixDetails = async (matrixId) => {
      if (matrixId) {
        await approvalMatrix(matrixId);
      } else {
        matrix.value = null;
      }
    };

    const sendDocument = handleSubmit(async (values) => {
      isSaving.value = true;

      const routeData = router.resolve({
        name: "approval-process-preview",
        params: {
          id: 'item_id_value',
        },
        query: {
          referenceable_type: 'referenceable_type_value',
          referenceable_id: 'referenceable_id_value',
        }
      });

      const fullUrl = window.location.origin + routeData.href

      try {
        await store.dispatch("sendDocumentApproval", {
          to:to.value.value,
          cc:cc.value.value,
          subject: values.subject,
          date: formattedDate(values.date),
          message: values.message,
          draft: values.draft,
          doc_ids: doc_ids.value,
          approval_matrix_id: values.approval_matrix_id,
          full_url: fullUrl,
        });

        toast.success("Document sent for approval successfully.");
        store.dispatch("getDocumentsList");
        resetFormData();
        localDialog.value = false;
        emit("refresh");
      } catch {
        toast.error("Failed to send document for approval.");
      } finally {
        isSaving.value = false;
      }
    });

    const closeDialog = () => {
      if (isSaving.value || isUploading.value) return;
      localDialog.value = false;
    };

    const downloadFile = async (file, type) => {
      try {
        const blob = await store.dispatch( "downlosdCmsFile", { fileId: file.id, type: type } );

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = file.original_name;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(link.href);

      } catch (error) {
        console.error("Download failed", error);
        alert("File download failed");
      }
    };

    const getFileIcon = (file) => {
      const ext = file.stored_name.split(".").pop().toLowerCase();

      switch (ext) {
        case "pdf":
          return "mdi mdi-file-pdf-box";
        case "doc":
        case "docx":
          return "mdi mdi-file-word";
        case "xls":
        case "xlsx":
        case "csv":
          return "mdi mdi-microsoft-excel";
        case "jpg":
        case "jpeg":
        case "png":
          return "mdi mdi-image-album";
        case "ppt":
        case "pptx":
          return "mdi mdi-file-powerpoint";
        case "zip":
        case "rar":
        case "7z":
          return "mdi mdi-folder-zip";
        case "txt":
          return "mdi mdi-file-document-outline";
        default:
          return "mid mdi-file";
      }
    };

    const getFileColor = (file) => {
      const ext = file.stored_name.split(".").pop().toLowerCase();

      switch (ext) {
        case "pdf":
          return "red";
        case "doc":
        case "docx":
          return "blue";
        case "xls":
        case "xlsx":
        case "csv":
          return "green";
        case "jpg":
        case "jpeg":
        case "png":
          return "purple";
        case "ppt":
        case "pptx":
          return "orange";
        case "zip":
        case "rar":
        case "7z":
          return "brown";
        case "txt":
          return "grey";
        default:
          return "grey";
      }
    };


    return {
      localDialog,
      formValid,
      subject,
      to,
      cc,
      showCc,
      showBcc,
      date,
      message,
      draft,
      selectedFile,
      matrix,
      isSaving,
      isUploading,
      approval_matrix_id,
      approvalMatrixList,
      getApprovalMatrixList,
      emailSuggestions,
      sendDocument,
      approvalMatrix,
      fetchApprovalMatrices,
      fetchApprovalMatrixDetails,
      formatApprovalMatrixItem,
      closeDialog,
      downloadFile,
      getFileIcon,
      getFileColor,
      ClassicEditor,
      editorConfig: { language: "en" },
      is_mail,
    };
  },

   mounted() {
    this.$store.dispatch('getUsersList');
    this.date.value.value = new Date();
    this.fetchApprovalMatrices();
  },
};
</script>

<style scoped>
.v-card-text {
  max-height: 60vh;
  overflow-y: auto;
}

.file-card {
  transition: all 0.2s ease;
}

.file-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #1976d2;
}
</style>
